import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Box,
  Typography,
  Autocomplete,
  TextField,
  CircularProgress,
  Alert,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Chip,
} from "@mui/material";
import { HiSearch, HiCheck } from "react-icons/hi";
import { useSnackbar } from "notistack";
import {
  useAdminOrganizationsSearchPaginated,
  useAdminRolesByOrg,
  useAdminUserSearch,
  useAdminCreateAssignment,
} from "@/api/hooks/admin.hooks";
import type { OrganizationDto } from "@/api/types/dtos";
import type { RoleDto } from "@/api/types/dtos/role-privilege.dto";

interface AssignUserModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  initialOrganizationId?: string;
}

const STEPS = ["Organización", "Rol", "Usuario"];

const AssignUserModal: React.FC<AssignUserModalProps> = ({
  open,
  onClose,
  onSuccess,
  initialOrganizationId,
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const [activeStep, setActiveStep] = useState(initialOrganizationId ? 1 : 0);
  const [selectedOrg, setSelectedOrg] = useState<OrganizationDto | null>(null);
  const [selectedRole, setSelectedRole] = useState<RoleDto | null>(null);
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [debouncedUserSearch, setDebouncedUserSearch] = useState("");
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedUserName, setSelectedUserName] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);

  const [orgSearch, setOrgSearch] = useState("");
  const [debouncedOrgSearch, setDebouncedOrgSearch] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedOrgSearch(orgSearch), 300);
    return () => clearTimeout(timer);
  }, [orgSearch]);

  const { data: orgsPaginatedData, isLoading: orgsLoading } = useAdminOrganizationsSearchPaginated({
    page: 0,
    size: 20,
    search: debouncedOrgSearch || undefined,
  });

  const { data: rolesData, isLoading: rolesLoading } = useAdminRolesByOrg(selectedOrg?.id || "");

  const { data: usersData, isLoading: usersLoading } = useAdminUserSearch(debouncedUserSearch);

  const createAssignment = useAdminCreateAssignment();

  const organizations: OrganizationDto[] = orgsPaginatedData?.data?.content ?? [];
  const roles: RoleDto[] = (rolesData?.data ?? []) as RoleDto[];
  const users = (usersData?.data ?? []) as Array<{
    id: string;
    name: string;
    lastName?: string;
    idNumber: string;
    email?: string;
  }>;

  useEffect(() => {
    if (open && initialOrganizationId && organizations.length > 0) {
      const found = organizations.find((o) => o.id === initialOrganizationId);
      if (found) {
        setSelectedOrg(found);
        setActiveStep(1);
      }
    }
  }, [open, initialOrganizationId, organizations]);

  useEffect(() => {
    if (!userSearchTerm || userSearchTerm.length < 2) {
      setDebouncedUserSearch("");
      return;
    }
    const timer = setTimeout(() => {
      setDebouncedUserSearch(userSearchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [userSearchTerm]);

  const handleReset = useCallback(() => {
    if (initialOrganizationId) {
      const found = organizations.find((o) => o.id === initialOrganizationId);
      setSelectedOrg(found || null);
      setActiveStep(found ? 1 : 0);
    } else {
      setActiveStep(0);
      setSelectedOrg(null);
    }
    setSelectedRole(null);
    setUserSearchTerm("");
    setDebouncedUserSearch("");
    setSelectedUserId(null);
    setSelectedUserName("");
    setSubmitting(false);
  }, [initialOrganizationId, organizations]);

  useEffect(() => {
    if (open) {
      handleReset();
    }
  }, [open, handleReset]);

  const handleNext = () => {
    setActiveStep((prev) => Math.min(prev + 1, STEPS.length - 1));
  };

  const handleOrgChange = (_: unknown, value: OrganizationDto | null) => {
    setSelectedOrg(value);
    setSelectedRole(null);
    setSelectedUserId(null);
    setSelectedUserName("");
    setUserSearchTerm("");
    setDebouncedUserSearch("");
  };

  const handleRoleChange = (_: unknown, value: RoleDto | null) => {
    setSelectedRole(value);
    setSelectedUserId(null);
    setSelectedUserName("");
  };

  const handleUserSelect = (userId: string, userName: string) => {
    if (selectedUserId === userId) {
      setSelectedUserId(null);
      setSelectedUserName("");
    } else {
      setSelectedUserId(userId);
      setSelectedUserName(userName);
    }
  };

  const handleSubmit = async () => {
    if (!selectedOrg || !selectedRole || !selectedUserId) return;

    setSubmitting(true);
    try {
      await createAssignment.mutateAsync({
        userId: selectedUserId,
        roleId: selectedRole.id,
        organizationId: selectedOrg.id,
      });
      enqueueSnackbar("Asignación creada exitosamente", { variant: "success" });
      onSuccess();
      onClose();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { description?: string } } };
      const message =
        err?.response?.data?.description || "Error al crear la asignación. Intente nuevamente.";
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const canProceed = () => {
    if (activeStep === 0) return !!selectedOrg;
    if (activeStep === 1) return !!selectedRole;
    if (activeStep === 2) return !!selectedUserId;
    return false;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="assign-user-modal-title"
      role="dialog"
      aria-modal="true"
    >
      <DialogTitle id="assign-user-modal-title">Asignar Usuario</DialogTitle>

      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 3, mt: 1 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {activeStep === 0 && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Seleccione la organización a la que desea asignar un usuario.
            </Typography>
            <Autocomplete
              options={organizations}
              getOptionLabel={(option) => option.name || ""}
              value={selectedOrg}
              onChange={handleOrgChange}
              onInputChange={(_, value) => setOrgSearch(value)}
              loading={orgsLoading}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              filterOptions={(x) => x}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Organización"
                  placeholder="Buscar organización..."
                  error={activeStep === 0 && !selectedOrg}
                  helperText={
                    activeStep === 0 && !selectedOrg ? "Seleccione una organización" : " "
                  }
                  id="assign-org-select"
                  inputProps={{
                    ...params.inputProps,
                    "aria-invalid": activeStep === 0 && !selectedOrg,
                    "aria-describedby":
                      activeStep === 0 && !selectedOrg ? "assign-org-select-error" : undefined,
                  }}
                  FormHelperTextProps={{ id: "assign-org-select-error" }}
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <HiSearch size={16} style={{ opacity: 0.5 }} />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              )}
              disabled={!!initialOrganizationId && !!selectedOrg}
            />
          </Box>
        )}

        {activeStep === 1 && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Roles de <strong>{selectedOrg?.name}</strong>
            </Typography>
            {rolesLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : roles.length === 0 ? (
              <Alert severity="info" sx={{ mt: 2 }}>
                Esta organización no tiene roles configurados. Cree roles antes de asignar usuarios.
              </Alert>
            ) : (
              <Autocomplete
                options={roles}
                getOptionLabel={(option) => option.name || ""}
                value={selectedRole}
                onChange={handleRoleChange}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Rol"
                    placeholder="Buscar rol..."
                    error={activeStep === 1 && !selectedRole}
                    helperText={activeStep === 1 && !selectedRole ? "Seleccione un rol" : " "}
                    id="assign-role-select"
                    inputProps={{
                      ...params.inputProps,
                      "aria-invalid": activeStep === 1 && !selectedRole,
                      "aria-describedby":
                        activeStep === 1 && !selectedRole ? "assign-role-select-error" : undefined,
                    }}
                    FormHelperTextProps={{ id: "assign-role-select-error" }}
                  />
                )}
              />
            )}
          </Box>
        )}

        {activeStep === 2 && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Busque y seleccione un usuario para asignar como <strong>{selectedRole?.name}</strong>{" "}
              en <strong>{selectedOrg?.name}</strong>.
            </Typography>
            <TextField
              fullWidth
              label="Buscar usuario"
              placeholder="Escriba nombre o documento..."
              value={userSearchTerm}
              onChange={(e) => setUserSearchTerm(e.target.value)}
              size="small"
              sx={{ mb: 2 }}
              id="assign-user-search"
              error={
                activeStep === 2 &&
                !selectedUserId &&
                debouncedUserSearch.length >= 2 &&
                users.length === 0
              }
              inputProps={{
                "aria-invalid":
                  activeStep === 2 &&
                  !selectedUserId &&
                  debouncedUserSearch.length >= 2 &&
                  users.length === 0,
                "aria-describedby":
                  activeStep === 2 &&
                  !selectedUserId &&
                  debouncedUserSearch.length >= 2 &&
                  users.length === 0
                    ? "assign-user-search-error"
                    : activeStep === 2 && userSearchTerm.length > 0 && userSearchTerm.length < 2
                      ? "assign-user-search-hint"
                      : undefined,
              }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      {usersLoading ? (
                        <CircularProgress size={16} />
                      ) : (
                        <HiSearch size={16} style={{ opacity: 0.5 }} />
                      )}
                    </InputAdornment>
                  ),
                },
              }}
              helperText={
                userSearchTerm.length > 0 && userSearchTerm.length < 2
                  ? "Escriba al menos 2 caracteres para buscar"
                  : activeStep === 2 &&
                      !selectedUserId &&
                      debouncedUserSearch.length >= 2 &&
                      users.length === 0
                    ? "No se encontraron usuarios. Intente con otro término."
                    : " "
              }
              FormHelperTextProps={{
                id:
                  userSearchTerm.length > 0 && userSearchTerm.length < 2
                    ? "assign-user-search-hint"
                    : "assign-user-search-error",
              }}
            />
            {debouncedUserSearch && !usersLoading && users.length === 0 && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: "center", py: 2 }}
              >
                No se encontraron usuarios
              </Typography>
            )}
            {users.length > 0 && (
              <List
                dense
                sx={{
                  maxHeight: 240,
                  overflow: "auto",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                }}
              >
                {users.map((user) => {
                  const fullName = [user.name, user.lastName].filter(Boolean).join(" ");
                  const isSelected = selectedUserId === user.id;
                  return (
                    <ListItemButton
                      key={user.id}
                      selected={isSelected}
                      onClick={() => handleUserSelect(user.id, fullName)}
                      sx={{
                        "&.Mui-selected": {
                          bgcolor: "primary.50",
                          "&:hover": { bgcolor: "primary.100" },
                        },
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            {fullName}
                            {isSelected && <HiCheck size={16} color="primary" />}
                          </Box>
                        }
                        secondary={
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              alignItems: "center",
                              mt: 0.5,
                            }}
                          >
                            <Chip
                              label={user.idNumber}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: "0.7rem" }}
                            />
                            {user.email && (
                              <Typography variant="caption" color="text.secondary">
                                {user.email}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            )}
            {selectedUserId && (
              <Alert severity="success" sx={{ mt: 2 }}>
                Usuario seleccionado: <strong>{selectedUserName}</strong>
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={
            activeStep === 0 ? handleClose : () => setActiveStep((prev) => Math.max(prev - 1, 0))
          }
          disabled={submitting}
        >
          {activeStep === 0 ? "Cancelar" : "Atrás"}
        </Button>
        {activeStep < 2 ? (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={!canProceed() || (activeStep === 1 && roles.length === 0 && !rolesLoading)}
          >
            Siguiente
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!canProceed() || submitting}
            startIcon={submitting ? <CircularProgress size={16} /> : undefined}
          >
            {submitting ? "Asignando..." : "Asignar"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AssignUserModal;
